import * as THREE from "three";
import type { KnightActor } from "./KnightActor";

/** Articulated procedural koi. Head, trunk, tail stock and tail are separate
 * joints so the silhouette bends instead of flying like one rigid mesh. */
export class KoiActor implements KnightActor {
  readonly object = new THREE.Group();
  private pitchRoot = new THREE.Group();
  private headJoint = new THREE.Group();
  private trunkJoint = new THREE.Group();
  private tailJoint = new THREE.Group();
  private tailFin = new THREE.Group();
  private leftFin = new THREE.Group();
  private rightFin = new THREE.Group();
  private dorsalFin = new THREE.Group();
  private rearLeftFin = new THREE.Group();
  private rearRightFin = new THREE.Group();
  private orange = new THREE.MeshStandardMaterial({ color: 0xf28c45, roughness: .38, metalness: .07 });
  private white = new THREE.MeshStandardMaterial({ color: 0xffead1, roughness: .48 });
  private black = new THREE.MeshBasicMaterial({ color: 0x101010 });
  private geometries: THREE.BufferGeometry[] = [];

  constructor(profile: "koi"|"catfish" = "koi") {
    const isKoi = profile === "koi";
    this.object.name = isKoi ? "KoiPerformer" : "CatfishPerformer";
    this.object.add(this.pitchRoot);

    // Koi are laterally compressed: narrow from above (x), but deep in
    // profile (y). The former rig had these proportions reversed.
    const headG = new THREE.SphereGeometry(.22, 22, 14); headG.scale(isKoi?.58:.78,isKoi?.72:.48,isKoi?.72:.82); this.geometries.push(headG);
    this.pitchRoot.add(this.headJoint);
    const head = new THREE.Mesh(headG, this.orange); head.position.set(0,.17,.19); this.headJoint.add(head);
    if (isKoi) {
      // Only a subtle muzzle; it should merge into the forehead rather than
      // read as a separate duck bill.
      const snoutG = new THREE.SphereGeometry(.068,18,12); snoutG.scale(.70,.70,.72); this.geometries.push(snoutG);
      const snout = new THREE.Mesh(snoutG,this.orange); snout.position.set(0,.158,.318); this.headJoint.add(snout);
    }

    this.trunkJoint.position.set(0,.17,.04); this.pitchRoot.add(this.trunkJoint);
    const trunkG = new THREE.SphereGeometry(.22, 22, 14); trunkG.scale(isKoi?.52:.68,isKoi?.76:.38,isKoi?.96:1.22); this.geometries.push(trunkG);
    const trunk = new THREE.Mesh(trunkG,this.orange); trunk.position.z=isKoi?-.17:-.22; this.trunkJoint.add(trunk);

    this.tailJoint.position.z=isKoi?-.34:-.43; this.trunkJoint.add(this.tailJoint);
    const stockG = new THREE.SphereGeometry(.14,16,10); stockG.scale(isKoi?.48:.62,isKoi?.62:.31,isKoi?.78:.95); this.geometries.push(stockG);
    const stock = new THREE.Mesh(stockG,this.orange); stock.position.z=isKoi?-.09:-.12; this.tailJoint.add(stock);
    this.tailFin.position.z=isKoi?-.21:-.27; this.tailJoint.add(this.tailFin);
    const tailG = new THREE.ConeGeometry(isKoi?.19:.22,isKoi?.35:.42,3); tailG.rotateX(Math.PI/2); this.geometries.push(tailG);
    const tail = new THREE.Mesh(tailG,this.orange); tail.position.z=-.12; tail.rotation.z=Math.PI/2; this.tailFin.add(tail);

    const finG = new THREE.ConeGeometry(.09,.27,3); this.geometries.push(finG);
    const left = new THREE.Mesh(finG,this.orange), right = new THREE.Mesh(finG,this.orange);
    this.leftFin.position.set(-.17,.13,.08); this.rightFin.position.set(.17,.13,.08);
    left.rotation.z=.95; right.rotation.z=-.95; this.leftFin.add(left); this.rightFin.add(right);
    this.pitchRoot.add(this.leftFin,this.rightFin);

    // Short dorsal fin, deliberately modest so it reads as koi rather than
    // a shark. It follows the articulated trunk.
    const dorsalG = new THREE.ConeGeometry(.085,.20,3); this.geometries.push(dorsalG);
    const dorsal = new THREE.Mesh(dorsalG,this.orange); this.dorsalFin.add(dorsal);
    this.dorsalFin.position.set(0,.29,-.18); this.trunkJoint.add(this.dorsalFin);
    this.dorsalFin.visible=isKoi;

    // Small paired fins beside the caudal peduncle.
    const rearFinG = new THREE.ConeGeometry(.045,.14,3); this.geometries.push(rearFinG);
    const rearLeft = new THREE.Mesh(rearFinG,this.orange), rearRight = new THREE.Mesh(rearFinG,this.orange);
    rearLeft.rotation.z=.92; rearRight.rotation.z=-.92;
    this.rearLeftFin.position.set(-.095,.02,-.25); this.rearRightFin.position.set(.095,.02,-.25);
    this.rearLeftFin.add(rearLeft); this.rearRightFin.add(rearRight);
    this.tailJoint.add(this.rearLeftFin,this.rearRightFin);
    this.rearLeftFin.visible=this.rearRightFin.visible=isKoi;

    const eyeG=new THREE.SphereGeometry(.025,8,6); this.geometries.push(eyeG);
    const e1=new THREE.Mesh(eyeG,this.black),e2=new THREE.Mesh(eyeG,this.black);
    e1.position.set(-.085,.245,.30); e2.position.set(.085,.245,.30); this.headJoint.add(e1,e2);
    // Keep the complete silhouette comfortably inside one board square.
    this.object.scale.setScalar(1.04);
    this.object.traverse(o=>{if((o as THREE.Mesh).isMesh)(o as THREE.Mesh).castShadow=true});
  }

  setPosition(p:THREE.Vector3):void { this.object.position.copy(p); }
  setYaw(y:number):void { this.object.rotation.y=y; }
  setPitch(p:number):void { this.pitchRoot.rotation.x=p; }
  setSwimPose(pitch:number, roll:number, bend:number, tail:number, fin:number):void {
    this.pitchRoot.rotation.x=pitch;
    this.pitchRoot.rotation.z=roll;
    this.headJoint.rotation.y=-bend*.18;
    this.headJoint.rotation.x=-Math.abs(bend)*.08;
    this.trunkJoint.rotation.y=bend*.42;
    this.trunkJoint.rotation.x=Math.sin(tail*.8)*.055;
    this.tailJoint.rotation.y=bend*.82 + tail*.22;
    this.tailFin.rotation.y=bend*1.12 + tail*.62;
    this.tailFin.rotation.z=tail*.12;
    // Independent pectoral fins add two small degrees of freedom and
    // deliberately avoid perfect mirrored motion.
    this.leftFin.rotation.z=fin + tail*.09;
    this.rightFin.rotation.z=-fin + tail*.07;
    this.leftFin.rotation.x=tail*.18;
    this.rightFin.rotation.x=-tail*.14;
    this.dorsalFin.rotation.z=Math.sin(tail*.7)*.045;
    this.rearLeftFin.rotation.z=tail*.13;
    this.rearRightFin.rotation.z=-tail*.11;
    this.rearLeftFin.rotation.x=tail*.2;
    this.rearRightFin.rotation.x=-tail*.17;
  }
  dispose():void { this.geometries.forEach(g=>g.dispose()); this.orange.dispose(); this.white.dispose(); this.black.dispose(); this.object.clear(); }
}

/** Preserves the earlier, flatter and endearingly catfish-like silhouette. */
export class CatfishActor extends KoiActor {
  constructor(){ super("catfish"); }
}
